import { useDeviceType } from '@cian/ui-kit';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { trackCalculateClick } from './tracking';
import { calculate } from '../../actions/calculate';
import { CalculatorForm } from '../../components/CalculatorForm';
import { selectCalculateResult } from '../../selectors/calculate';
import {
  selectCalculatorControlsDisabled,
  selectCalculatorErrorMessage,
  selectCalculatorLoading,
} from '../../selectors/calculator';
import { TThunkDispatch } from '../../types/redux/thunk';
import { useApplicationContext } from '../../utils/applicationContext';
import { CalculateResultContainer } from '../CalculateResultContainer';
import { Address } from '../fields/Address';
import { District } from '../fields/District';
import { FlatPrice } from '../fields/FlatPrice';
import { MoneyBalance } from '../fields/MoneyBalance';
import { MoneyToSpend } from '../fields/MoneyToSpend';
import { RentPrice } from '../fields/RentPrice';
import { Rooms } from '../fields/Rooms';

export const Calculator: React.FC = () => {
  const { config } = useApplicationContext();
  const deviceType = useDeviceType();
  const dispatch = useDispatch<TThunkDispatch>();
  const isLoading = useSelector(selectCalculatorLoading);
  const isControlsDisabled = useSelector(selectCalculatorControlsDisabled);
  const errorMessage = useSelector(selectCalculatorErrorMessage);
  const calculateResult = useSelector(selectCalculateResult);
  const scrollableContent = React.useRef<HTMLDivElement>(null);
  // TODO remove feature toggle https://jira.cian.tech/browse/CD-126766
  const isGeoSuggestEnabled = config.get<boolean>('investment-calculator-frontend.geoSuggest.enabled');

  const handleSubmit = React.useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      trackCalculateClick();

      const result = await dispatch(calculate());

      if (deviceType === 'desktop') {
        if (scrollableContent.current) {
          scrollableContent.current.scrollTo({
            top: scrollableContent.current.scrollHeight,
            behavior: 'smooth',
          });
        }
      } else {
        if (!result) {
          document.documentElement.scrollTo({
            top: document.documentElement.scrollHeight,
            behavior: 'smooth',
          });
        }
      }
    },
    [dispatch, deviceType],
  );

  return (
    <CalculatorForm
      contentFooter={deviceType === 'desktop' && calculateResult && <CalculateResultContainer />}
      scrollableContent={scrollableContent}
      errorMessage={errorMessage}
      loading={isLoading}
      disabled={isControlsDisabled}
      onSubmit={handleSubmit}
    >
      {isGeoSuggestEnabled && <Address />}
      {!isGeoSuggestEnabled && <District />}
      <Rooms />
      <FlatPrice isGeoSuggestEnabled />
      <MoneyBalance />
      <RentPrice />
      <MoneyToSpend />
    </CalculatorForm>
  );
};
